import React from 'react';
import clsx from 'clsx';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import Button from '@material-ui/core/Button';
import { TFunction } from 'i18next';
import { PackageDescriptionList } from '../lists';
import MinekoButton from './Button';
import { Input, InputWrapper } from '../inputs';
import { ErrorMessage } from '../text';
import { FormControlLabel, Link, Checkbox } from '@material-ui/core';
import { Trans } from 'react-i18next';

const useStyles = makeStyles((theme) =>
  createStyles({
    wrapper: {
      flex: '1 0 0',
      position: 'relative',
      margin: '25px 10px 0',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        margin: '35px 0 15px',
      },
    },
    root: {
      display: 'flex',
      cursor: 'pointer',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      padding: 13,
      width: '100%',
      minHeight: '300px',
      border: `1px solid ${theme.palette.secondary.main}`,
      borderRadius: 10,
      background: theme.palette.secondary.contrastText,
      color: theme.palette.text.primary,
      textAlign: 'left',
      fontSize: 18,
      fontFamily: 'Open Sans, sans-serif',
      fontWeight: 'bold',
      lineHeight: 1.75,
      letterSpacing: '-0.3px',
      textTransform: 'none',
      '&:hover, &.active': {
        border: `1px solid ${theme.palette.secondary.main}`,
        boxShadow: `inset 0px 0px 0px 2px ${theme.palette.secondary.main}`,
        background: theme.extraColors.secondaryHover,
      },
      '&.Mui-disabled': {
        color: theme.palette.secondary.main,
        opacity: 0.4,
      },
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        minHeight: 'unset',
      },
    },
    rootMinHeight: {
      minHeight: '100%',
    },
    heading: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: 18,
    },
    logo: {
      height: 40,
      width: 'auto',
    },
    content: {
      width: '100%',
      minHeight: '300px',
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      [theme.breakpoints.down('xs')]: {
        height: '100%',
        minHeight: 'unset',
      },
    },
    contentMinHeight: {
      minHeight: 340,
      [theme.breakpoints.down('xs')]: {
        minHeight: 'unset',
      },
    },
    title: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      alignItems: 'center',
      color: theme.palette.secondary.main,
      marginBottom: 5,
      [theme.breakpoints.down('xs')]: {
        marginRight: 6,
      },
    },
    pricing: {
      marginBottom: 5,
      fontSize: 50,
    },
    currencySymbol: {
      marginRight: 2,
      fontSize: 38,
      verticalAlign: 'text-bottom',
    },
    currencyText: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.text.primary,
      [theme.breakpoints.down('xs')]: {
        fontSize: 10,
      },
    },
    list: {
      color: theme.palette.text.primary,
      fontSize: 12,
      fontWeight: 400,
      paddingLeft: 16,
      lineHeight: '18px',
      [theme.breakpoints.down('xs')]: {
        marginTop: 0,
        fontSize: 10,
        lineHeight: '16px',
      },
    },
    productName: {
      [theme.breakpoints.down('sm')]: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        width: 170,
        textAlign: 'center',
      },
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
    },

    nonSelectable: {
      display: 'none',
      position: 'absolute',
      bottom: 13,
      left: 26,
      fontSize: 10,
      fontWeight: 700,
      background: theme.palette.error.main,
      color: theme.palette.common.white,
      lineHeight: '15px',
      padding: '0 6px',
      borderRadius: 5,
      '&.active': {
        display: 'block',
      },
      [theme.breakpoints.down('xs')]: {
        bottom: 7,
        left: 42,
      },
    },
    bannerWrapper: {
      fontSize: 12,
      fontWeight: 600,
      textAlign: 'center',
      paddingBottom: 5,
      position: 'absolute',
      width: '100%',
    },
    banner: {
      backgroundColor: theme.palette.grey['400'],
      display: 'inline-block',
      padding: '1px 10px',
      borderRadius: '3px',
    },
    bannerHighlighted: {
      backgroundColor: theme.extraColors.productHighlight,
      color: 'white',
    },
    submitButtonWrapper: {
      marginTop: 'auto',
      width: '100%',
    },
    legalInsuranceInputWrapperContainer: {
      marginTop: 15,
      maxWidth: 400,
      zIndex: 1000,
      [theme.breakpoints.down('xs')]: {
        maxWidth: '100%',
      },
    },
    legalInsuranceInputWrapper: {
      paddingTop: '0px',
      paddingBottom: '0px',
      marginBottom: '10px',
      [theme.breakpoints.down('xs')]: {
        padding: 0,
      },
    },
    errorWrapper: {
      width: 'fit-content',
      paddingBottom: '15px',
      [theme.breakpoints.down('xs')]: {
        marginLeft: '14px',
      },
    },
    logoCompensation: {
      height: 40,
      [theme.breakpoints.down('xs')]: {
        height: 'unset',
      },
    },
    checkboxContainer: {
      paddingLeft: 8,
      paddingRight: 8,
    },
    checkbox: {
      position: 'relative',
      marginBottom: 20,
      fontSize: 12,
    },
    checkboxLabelRoot: {
      alignItems: 'flex-start',
    },
    checkboxLabel: {
      fontSize: 12,
    },
    checkboxError: {
      border: `1px solid ${theme.palette.error.main}`,
      borderRadius: 3,
    },
    checkboxRoot: {
      marginTop: -8,
    },
  }),
);

const TopBanner: React.FC<{ highlighted?: boolean; parentAdditionalMargin?: number }> = ({
  highlighted,
  children,
  parentAdditionalMargin,
}: any) => {
  const classes = useStyles();
  return (
    <div
      className={classes.bannerWrapper}
      style={{ bottom: parentAdditionalMargin ? `calc(100% - ${parentAdditionalMargin}px)` : '100%' }}
    >
      <span className={clsx(classes.banner, highlighted && classes.bannerHighlighted)}>{children}</span>
    </div>
  );
};

interface Props {
  onClick: (props: any) => void;
  t: TFunction;
  product: {
    priceGross?: string;
    initialPriceGross?: string;
    logo?: string;
    name: string;
    descriptions: Array<{ id: string; description: string }>;
    descriptionsNotIncluded: Array<{ id: string; description: string }>;
  };
  isActive?: boolean;
  disabled?: boolean;
  unavailable?: boolean;
  highlighted?: boolean;
  hidePrice?: boolean;
  error?: string;
  referenceLabel?: string;
  onReferenceChange?: (event: any) => void;
  reference?: string;
  legalInsuranceConsent?: boolean;
  logoCompensation?: boolean;
  onLegalInsuranceConsentChange?: (event: any, checked: boolean) => void;
  legalInsuranceConsentError?: string;
  requiresInsuranceConsent?: boolean;
  requiresBillingConsent?: boolean;
  requiresUnionOptionsConsent?: boolean;
  billingConsent?: boolean;
  onBillingConsentChange?: (event: any, checked: boolean) => void;
  billingConsentError?: string;
  unionOptionsConsent?: boolean;
  unionOptionsConsentError?: string;
  onUnionOptionsChange?: (event: any, checked: boolean) => void;
}

const DEFAULT_INSURANCE_POLICY_TEXT = 'Baustein Wohnen/Immobilie';

const PackageChoiceButton: React.FC<Props> = ({
  onClick,
  t,
  product,
  disabled = false,
  unavailable = false,
  highlighted = false,
  hidePrice = false,
  error,
  referenceLabel,
  onReferenceChange,
  reference,
  logoCompensation,
  legalInsuranceConsent,
  onLegalInsuranceConsentChange,
  legalInsuranceConsentError,
  requiresInsuranceConsent,
  requiresBillingConsent,
  billingConsent,
  onBillingConsentChange,
  billingConsentError,
  requiresUnionOptionsConsent,
  unionOptionsConsent,
  unionOptionsConsentError,
  onUnionOptionsChange,
}) => {
  const classes = useStyles();
  const showReferenceInput = !!onReferenceChange && !!referenceLabel;

  return (
    <div className={classes.wrapper} data-cy="package-choice-button">
      <div className={logoCompensation && classes.logoCompensation}></div>
      {unavailable && (
        <TopBanner parentAdditionalMargin={logoCompensation && 40}>{t('component.unavailable')}</TopBanner>
      )}
      {highlighted && (
        <TopBanner highlighted parentAdditionalMargin={logoCompensation && 40}>
          {t('component.highlighted')}
        </TopBanner>
      )}
      <div
        onClick={(e) => onClick(e)}
        className={clsx([classes.root, product.logo && classes.rootMinHeight])}
        style={{ height: showReferenceInput ? '100%' : undefined }}
      >
        <div className={clsx([classes.content, product.logo && classes.contentMinHeight])}>
          <div className={classes.heading}>
            <div className={classes.title}>
              {product.logo && <img className={classes.logo} src={product.logo} alt=" " />}
              <span className={classes.productName} title={product.name}>
                {product.name}
              </span>
            </div>

            <div className={classes.pricing} data-cy="package-choice-button__price-paragraph">
              <span className={classes.currencySymbol}>&euro; </span>
              <span data-cy="package-choice-button__price">
                {!hidePrice && product.priceGross}
                {(hidePrice || !product.priceGross) && '0'}
                {',-'}
              </span>
              {product.initialPriceGross && (
                <span className={classes.currencyText} data-cy="package-choice-button__initial-price">
                  {' '}
                  {t('component.instead')} (&euro;
                  {product.initialPriceGross})
                </span>
              )}
            </div>
          </div>
          <PackageDescriptionList active items={product.descriptions} />
          <PackageDescriptionList active={false} items={product.descriptionsNotIncluded} />

          {showReferenceInput && (
            <div
              className={clsx(classes.legalInsuranceInputWrapperContainer)}
              data-cy="reference-wrapper"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="errorWrapper">
                <InputWrapper className={classes.legalInsuranceInputWrapper}>
                  <Input
                    placeholder={
                      referenceLabel
                        ? t('package_reference_placeholder', { referenceLabel })
                        : t('package_free_client_number', {
                            title: product!.name.toUpperCase(),
                          })
                    }
                    label={referenceLabel || t('package_free_client_number_label')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    name="reference"
                    value={reference || ''}
                    error={!!error}
                    onChange={onReferenceChange}
                    smallPalceHolder
                  />
                </InputWrapper>
                <div className={classes.errorWrapper}>{!!error && <ErrorMessage>{error}</ErrorMessage>}</div>
              </div>
            </div>
          )}

          {requiresUnionOptionsConsent && (
            <div className={classes.checkboxContainer} onClick={(e) => e.stopPropagation()}>
              <div className={clsx([classes.checkbox, unionOptionsConsentError && classes.checkboxError])}>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={unionOptionsConsent}
                      onChange={onUnionOptionsChange}
                      classes={{ root: classes.checkboxRoot }}
                    />
                  }
                  label={t('unionOptionsConsentCheckboxText')}
                  classes={{
                    root: classes.checkboxLabelRoot,
                    label: classes.checkboxLabel,
                  }}
                />
              </div>
              {unionOptionsConsentError && (
                <ErrorMessage style={{ marginTop: -10, marginBottom: 10 }}>
                  {t('error.requiredCheckboxUnion')}
                </ErrorMessage>
              )}
            </div>
          )}
          {requiresInsuranceConsent && (
            <div className={classes.checkboxContainer} onClick={(e) => e.stopPropagation()}>
              <div className={clsx([classes.checkbox, legalInsuranceConsentError && classes.checkboxError])}>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={legalInsuranceConsent}
                      onChange={onLegalInsuranceConsentChange}
                      classes={{ root: classes.checkboxRoot }}
                    />
                  }
                  label={t('legalInsuranceCheckboxText', {
                    insurancePolicy: DEFAULT_INSURANCE_POLICY_TEXT,
                  })}
                  classes={{
                    root: classes.checkboxLabelRoot,
                    label: classes.checkboxLabel,
                  }}
                />
              </div>
              {legalInsuranceConsentError && (
                <ErrorMessage style={{ marginTop: -10, marginBottom: 10 }}>{t('error.requiredCheckbox')}</ErrorMessage>
              )}

              {requiresBillingConsent && (
                <div className={clsx([classes.checkbox, billingConsentError && classes.checkboxError])}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        value={billingConsent}
                        onChange={onBillingConsentChange}
                        classes={{ root: classes.checkboxRoot }}
                      />
                    }
                    classes={{ root: classes.checkboxLabelRoot, label: classes.checkboxLabel }}
                    label={
                      <Trans
                        t={t}
                        i18nKey="billingConsentCheckboxText"
                        components={{
                          Link: (
                            <Link
                              href="https://www.mineko.de/ra/impressum/"
                              rel="noopener noreferrer"
                              target="_blank"
                            />
                          ),
                        }}
                      />
                    }
                  />
                </div>
              )}
              {requiresBillingConsent && billingConsentError && (
                <ErrorMessage style={{ marginTop: -10, marginBottom: 10 }}>
                  {t('error.noCheckWithoutConsent')}
                </ErrorMessage>
              )}
            </div>
          )}
          <div className={classes.submitButtonWrapper}>
            <MinekoButton onClick={() => undefined} disabled={disabled || unavailable}>
              {hidePrice || !product.priceGross ? t('free_package_button_submit') : t('package_button_submit')}
            </MinekoButton>
          </div>
        </div>
      </div>
      <div className={clsx(classes.nonSelectable, disabled ? 'active' : '')}>{t('component.nonbookable')}</div>
    </div>
  );
};

export default PackageChoiceButton;
